import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { goEntitiesApi } from "@/services/go-entities.service";

export type CountryBadgeProps = {
  name?: string | null;
  iso?: string | null;
  ginfObjId?: number | null;
  className?: string;
  onOpen?: (ginfObjId: number) => void;
  /** Готовый blob/URL флага; если не задан — badge сам подтянет primary-flag. */
  flagUrl?: string | null;
};

/** Компактный бейдж страны для embed в РД/ОИ/УДГСПЗ и хабе ГО. */
export function CountryBadge({
  name,
  iso,
  ginfObjId,
  className,
  onOpen,
  flagUrl: flagUrlProp,
}: CountryBadgeProps) {
  const label = name?.trim() || (iso ? String(iso) : ginfObjId != null ? `#${ginfObjId}` : "—");
  const clickable = ginfObjId != null && onOpen != null;
  const [flagUrl, setFlagUrl] = useState<string | null>(flagUrlProp ?? null);

  useEffect(() => {
    if (flagUrlProp !== undefined) {
      setFlagUrl(flagUrlProp);
      return;
    }
    if (ginfObjId == null) {
      setFlagUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const flag = await goEntitiesApi.getPrimaryFlag(ginfObjId);
        if (!flag?.ginf_file_id || cancelled) return;
        const blob = await goEntitiesApi.downloadMaterialBlob(ginfObjId, flag.ginf_file_id);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setFlagUrl(objectUrl);
      } catch {
        if (!cancelled) setFlagUrl(null);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ginfObjId, flagUrlProp]);

  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs text-foreground",
        clickable && "cursor-pointer hover:bg-muted",
        className,
      )}
      title={iso ? `${label} (${iso})` : label}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={() => {
        if (clickable && ginfObjId != null) onOpen(ginfObjId);
      }}
      onKeyDown={(e) => {
        if (!clickable || ginfObjId == null) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(ginfObjId);
        }
      }}
    >
      {flagUrl ? (
        <img
          src={flagUrl}
          alt=""
          className="h-3.5 w-5 shrink-0 rounded-[2px] object-cover border border-border/60"
        />
      ) : null}
      {iso ? (
        <span className="font-mono text-[10px] uppercase text-muted-foreground">{iso}</span>
      ) : null}
      <span className="truncate">{label}</span>
    </span>
  );
}
